import React, { useEffect, useState, useContext } from 'react'
import { Card, Button, Row, Col, Table, Badge, Dropdown } from 'react-bootstrap'
import { useToasts } from 'react-toast-notifications'
import { AppContext } from '../../../app'
import { Role, SystemRole, CustomRole } from '../../../domain/usecases/Roles'
import { RemoteGetRoles } from '../../../data/usecases/Roles/RemoteGetRoles'
import { RemoteDeleteCustomRole } from '../../../data/usecases/Roles/RemoteDeleteCustomRole'
import { useAxiosHttpClient } from '../../../hooks/useAxiosHttpClient'
import { URL_API } from '../../../services/api_url'
import { EditCustomRoleModal } from './EditCustomRoleModal'
import { usePermissions } from '../../../hooks/usePermissions'

export const RoleManagementPage: React.FC = () => {
  const { user } = useContext(AppContext)
  const { addToast } = useToasts()
  const httpClient = useAxiosHttpClient()
  const { can } = usePermissions()

  const [roles, setRoles] = useState<Role[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedRole, setSelectedRole] = useState<CustomRole | null>(null)
  const [showEditModal, setShowEditModal] = useState(false)

  const companyId = user?.selectedContext?.companyId

  useEffect(() => {
    if (companyId) {
      loadRoles()
    }
  }, [companyId])

  const loadRoles = async () => {
    if (!companyId) return

    setLoading(true)
    try {
      const getRolesUseCase = new RemoteGetRoles(URL_API || '', httpClient)
      const rolesData = await getRolesUseCase.getRoles(companyId)
      setRoles(rolesData)
    } catch (error) {
      console.error('Error loading roles:', error)
      addToast('Erro ao carregar funções', {
        appearance: 'error',
        autoDismiss: true
      })
    } finally {
      setLoading(false)
    }
  }

  const handleEditRole = (role: CustomRole) => {
    setSelectedRole(role)
    setShowEditModal(true)
  }

  const handleDeleteRole = async (roleId: string, roleName: string) => {
    if (
      !window.confirm(
        `Tem certeza que deseja excluir a função "${roleName}"? Esta ação não pode ser desfeita.`
      )
    ) {
      return
    }

    try {
      const deleteUseCase = new RemoteDeleteCustomRole(
        URL_API || '',
        httpClient
      )
      await deleteUseCase.delete(roleId)

      addToast('Função excluída com sucesso!', {
        appearance: 'success',
        autoDismiss: true
      })

      loadRoles()
    } catch (error: any) {
      console.error('Error deleting role:', error)
      addToast(error.message || 'Erro ao excluir função', {
        appearance: 'error',
        autoDismiss: true
      })
    }
  }

  const handleEditSuccess = () => {
    const wasEditing = selectedRole !== null
    setShowEditModal(false)
    setSelectedRole(null)
    loadRoles()
    addToast(
      wasEditing
        ? 'Função atualizada com sucesso!'
        : 'Função criada com sucesso!',
      {
        appearance: 'success',
        autoDismiss: true
      }
    )
  }

  const systemRoles = roles.filter(
    (r): r is SystemRole => 'is_system' in r && r.is_system
  )
  const customRoles = roles.filter(
    (r): r is CustomRole => !('is_system' in r) || !r.is_system
  )

  if (!companyId) {
    return (
      <Card>
        <Card.Body>
          <p>Por favor, selecione uma empresa no menu superior.</p>
        </Card.Body>
      </Card>
    )
  }

  return (
    <>
      <Row>
        <Col>
          <Card>
            <Card.Header>
              <h5>Gestão de Funções</h5>
            </Card.Header>
            <Card.Body>
              {loading ? (
                <div className="text-center py-5">
                  <div className="spinner-border" role="status">
                    <span className="sr-only">Carregando...</span>
                  </div>
                </div>
              ) : (
                <>
                  {/* System Roles Section */}
                  <div className="mb-5">
                    <h6 className="mb-3">
                      <i className="feather icon-shield mr-2" />
                      Funções do Sistema
                      <Badge variant="info" className="ml-2">
                        Somente Leitura
                      </Badge>
                    </h6>
                    <Table responsive hover>
                      <thead>
                        <tr>
                          <th>Nome</th>
                          <th>Descrição</th>
                        </tr>
                      </thead>
                      <tbody>
                        {systemRoles.map((role) => (
                          <tr key={role.id}>
                            <td>
                              <strong>{role.display_name}</strong>
                            </td>
                            <td className="text-muted">
                              {role.description || 'Função padrão do sistema'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>

                  {/* Custom Roles Section */}
                  <div>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h6 className="mb-0">
                        <i className="feather icon-edit mr-2" />
                        Funções Personalizadas
                      </h6>
                      {can.create('roles') && (
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() => {
                            setSelectedRole(null)
                            setShowEditModal(true)
                          }}
                        >
                          <i className="feather icon-plus mr-2" />
                          Criar Função Personalizada
                        </Button>
                      )}
                    </div>
                    {customRoles.length === 0 ? (
                      <div className="text-center py-5">
                        <i
                          className="feather icon-users"
                          style={{ fontSize: '48px', color: '#ccc' }}
                        />
                        <p className="mt-3 text-muted">
                          Nenhuma função personalizada criada ainda
                        </p>
                        <p className="text-muted">
                          Crie funções personalizadas ao convidar usuários
                          selecionando &quot;+ Criar Função Personalizada&quot;
                        </p>
                      </div>
                    ) : (
                      <Table responsive hover>
                        <thead>
                          <tr>
                            <th>Nome</th>
                            <th>Descrição</th>
                            <th>Criado em</th>
                            <th className="text-right">Ações</th>
                          </tr>
                        </thead>
                        <tbody>
                          {customRoles.map((role) => (
                            <tr key={role.id}>
                              <td>
                                <strong>{role.display_name}</strong>
                              </td>
                              <td className="text-muted">
                                {role.description || 'Função personalizada'}
                              </td>
                              <td className="text-muted">
                                {new Date(role.created_at).toLocaleDateString(
                                  'pt-BR'
                                )}
                              </td>
                              <td className="text-right">
                                {(can.update('roles') ||
                                  can.delete('roles')) && (
                                  <Dropdown alignRight>
                                    <Dropdown.Toggle
                                      variant="link"
                                      className="btn-sm text-muted"
                                      id={`role-actions-${role.id}`}
                                    >
                                      <i className="feather icon-more-vertical" />
                                    </Dropdown.Toggle>
                                    <Dropdown.Menu>
                                      {can.update('roles') && (
                                        <Dropdown.Item
                                          onClick={() => handleEditRole(role)}
                                        >
                                          <i className="feather icon-edit mr-2" />
                                          Editar Permissões
                                        </Dropdown.Item>
                                      )}
                                      {can.update('roles') &&
                                        can.delete('roles') && (
                                          <Dropdown.Divider />
                                        )}
                                      {can.delete('roles') && (
                                        <Dropdown.Item
                                          onClick={() =>
                                            handleDeleteRole(
                                              role.id,
                                              role.display_name
                                            )
                                          }
                                          className="text-danger"
                                        >
                                          <i className="feather icon-trash-2 mr-2" />
                                          Excluir Função
                                        </Dropdown.Item>
                                      )}
                                    </Dropdown.Menu>
                                  </Dropdown>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    )}
                  </div>
                </>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {showEditModal && (
        <EditCustomRoleModal
          show={showEditModal}
          onHide={() => {
            setShowEditModal(false)
            setSelectedRole(null)
          }}
          onSuccess={handleEditSuccess}
          role={selectedRole}
        />
      )}
    </>
  )
}

export default RoleManagementPage
